import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Check, Star, Info, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier badge styling
const tierStyles = {
  essential: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', label: 'Essential' },
  popular: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Most Popular' },
  premium: { bg: 'bg-[#C41E3A]/5', text: 'text-[#C41E3A]', border: 'border-[#C41E3A]/30', label: 'Premium' }
};

export default function Services() {
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('standard');
  const [expandedPackage, setExpandedPackage] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const [packagesRes, addonsRes, membershipsRes] = await Promise.all([
        axios.get(`${API}/services/packages`),
        axios.get(`${API}/services/addons`),
        axios.get(`${API}/services/memberships`)
      ]);
      setPackages(packagesRes.data);
      setAddons(addonsRes.data);
      setMemberships(membershipsRes.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPackages = packages.filter(pkg => pkg.vehicle_category === activeCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center pt-20">
        <div className="w-6 h-6 border-2 border-[#C41E3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <main data-testid="services-page" className="bg-white pt-20">
      {/* Header */}
      <section className="py-16 md:py-20 text-center px-6 bg-[#f5f5f7]">
        <div className="max-w-[800px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-[#1d1d1f] tracking-tight mb-4">
              Detailing Packages
            </h1>
            <p className="text-base md:text-lg text-[#86868b] max-w-xl mx-auto mb-8">
              Professional interior and exterior detailing designed to restore, protect and maintain your vehicle.
            </p>
            
            {/* Vehicle Category Toggle */}
            <div className="inline-flex bg-white rounded-xl p-1 shadow-sm">
              <button
                onClick={() => setActiveCategory('standard')}
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeCategory === 'standard'
                    ? 'bg-[#C41E3A] text-white'
                    : 'text-[#424245] hover:text-[#1d1d1f]'
                }`}
              >
                Standard Vehicles
              </button>
              <button
                onClick={() => setActiveCategory('large')}
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeCategory === 'large'
                    ? 'bg-[#C41E3A] text-white'
                    : 'text-[#424245] hover:text-[#1d1d1f]'
                }`}
              >
                Large Vehicles
              </button>
            </div>
            <p className="text-xs text-[#86868b] mt-3">
              {activeCategory === 'standard' 
                ? 'Hatchback, Sedan, Coupe, SUV, Double Cab'
                : '7-Seater, Minibus, Van, Taxi, Commercial'
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Packages */}
      <section className="py-16 px-6">
        <div className="max-w-[1000px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filteredPackages.map((pkg, index) => {
              const tier = tierStyles[pkg.tier] || tierStyles.essential;
              const isExpanded = expandedPackage === pkg.id;
              
              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-white rounded-2xl overflow-hidden border-2 transition-all hover:shadow-lg ${
                    pkg.tier === 'popular' ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-100'
                  }`}
                  data-testid={`package-card-${pkg.id}`}
                >
                  {/* Header */}
                  <div className={`px-6 py-4 ${tier.bg}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-semibold uppercase tracking-wider ${tier.text}`}>
                        {tier.label}
                      </span>
                      {pkg.tier === 'popular' && (
                        <Star className="w-4 h-4 text-blue-500 fill-blue-500" />
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-[#1d1d1f]">{pkg.name}</h3>
                  </div>
                  
                  {/* Price */}
                  <div className="px-6 py-5 border-b border-gray-100">
                    <div className="flex items-end gap-1">
                      <span className="text-3xl font-bold text-[#1d1d1f]">R{pkg.base_price.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-sm text-[#86868b]">
                      <Clock className="w-4 h-4" />
                      {pkg.duration}
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div className="px-6 py-4">
                    <p className="text-sm text-[#424245] mb-3">{pkg.description}</p>
                    
                    {pkg.best_for && (
                      <div className="bg-[#f5f5f7] rounded-lg p-3 mb-4">
                        <p className="text-xs font-semibold text-[#1d1d1f]">Best for:</p>
                        <p className="text-xs text-[#86868b]">{pkg.best_for}</p>
                      </div>
                    )}
                    
                    {pkg.result && (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-[#1d1d1f]">Result:</p>
                        <p className="text-xs text-[#86868b]">{pkg.result}</p>
                      </div>
                    )}
                    
                    {/* What's Included Toggle */}
                    <button
                      onClick={() => setExpandedPackage(isExpanded ? null : pkg.id)}
                      className="flex items-center gap-1 text-xs font-medium text-[#C41E3A] hover:underline mb-3"
                    >
                      <Info className="w-3 h-3" />
                      {isExpanded ? 'Hide details' : "What's included"}
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    
                    {/* Expandable Includes List */}
                    {isExpanded && pkg.includes && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        className="space-y-1.5 mb-4"
                      >
                        {pkg.includes.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-[#424245]">
                            <Check className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </motion.ul>
                    )}
                  </div>
                  
                  {/* CTA */}
                  <div className="px-6 pb-6">
                    <Link
                      to={`/booking?package=${pkg.id}`}
                      className={`w-full py-3 rounded-xl text-sm font-semibold text-center block transition-colors ${
                        pkg.tier === 'popular'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : pkg.tier === 'premium'
                            ? 'bg-[#C41E3A] text-white hover:bg-[#a01830]'
                            : 'bg-[#1d1d1f] text-white hover:bg-black'
                      }`}
                      data-testid={`book-${pkg.id}`}
                    >
                      Book {pkg.name}
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Premium Add-ons */}
      <section className="py-16 md:py-20 bg-[#f5f5f7] px-6">
        <div className="max-w-[1000px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#1d1d1f] tracking-tight mb-2">
              Enhance Your Service
            </h2>
            <p className="text-sm text-[#86868b]">
              Add these premium treatments to any package
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {addons.map((addon, index) => (
              <motion.div
                key={addon.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl p-5 hover:shadow-md transition-shadow border border-gray-100"
                data-testid={`addon-${addon.id}`}
              >
                <p className="text-sm font-semibold text-[#1d1d1f] mb-1">{addon.name}</p>
                {addon.description && (
                  <p className="text-xs text-[#86868b] mb-3 line-clamp-2">{addon.description}</p>
                )}
                <p className="text-lg text-[#C41E3A] font-bold">+R{addon.price.toLocaleString()}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Memberships */}
      <section className="py-16 md:py-20 bg-white px-6">
        <div className="max-w-[800px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#1d1d1f] tracking-tight mb-2">
              Monthly Memberships
            </h2>
            <p className="text-sm text-[#86868b]">
              Save with regular care plans
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {memberships.map((membership, index) => (
              <motion.div
                key={membership.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`rounded-2xl p-6 transition-all ${
                  membership.id === 'gold' 
                    ? 'bg-[#C41E3A]/5 border-2 border-[#C41E3A]' 
                    : 'bg-[#f5f5f7] border border-gray-200'
                }`}
                data-testid={`membership-${membership.id}`}
              >
                {membership.id === 'gold' && (
                  <span className="text-xs font-semibold text-[#C41E3A] mb-2 block">Recommended</span>
                )}
                <h3 className="text-lg font-bold text-[#1d1d1f] mb-1">{membership.name}</h3>
                <p className="text-3xl font-bold text-[#1d1d1f] mb-2">
                  R{membership.price.toLocaleString()}
                  <span className="text-sm font-normal text-[#86868b]">/month</span>
                </p>
                <p className="text-sm text-[#86868b]">{membership.benefits}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-[#1d1d1f]">
        <div className="max-w-[600px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to restore your vehicle?
            </h2>
            <p className="text-sm text-gray-400 mb-8">
              Book online or WhatsApp us for a custom quote
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/booking"
                className="bg-[#C41E3A] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#a01830] transition-colors inline-flex items-center justify-center gap-2"
              >
                Book Now
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/27760784050?text=Hi%20MA%20Africa%2C%20I%27d%20like%20to%20enquire%20about%20detailing%20services"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-colors"
              >
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
